import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  // Check login status on mount
  useEffect(() => {
    const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
    setIsLoggedIn(loggedIn);
  }, []);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Products', path: '/products' },
    { label: 'Shop Now', path: '/ecom' },
    { label: 'Wellness Blog', path: '/wellness-blog' },
    { label: 'Sparsh', path: '/dashboard' },
    { label: 'Contact', path: '/contact' },
  ];

  const products = [
    {
      id: 1,
      name: 'Moringa Powder - 100g',
      price: 299,
      image: 'https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/259510fa1447af2896f61d3473620b43.png',
      path: '/ecom'
    },
    {
      id: 2,
      name: 'Moringa Tablets - 60 Count',
      price: 399,
      image: 'https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/ec501008434a48d659f10ea9ddd79ab9.png',
      path: '/ecom'
    }
  ];

  const handleShopNow = () => {
    navigate('/ecom');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate('/ecom');
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/402bb47b1b6fd7c4f36ade1bab6a66a4.jpeg"
              alt="NatureIQ"
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">Home</Link>
            <Link to="/about" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">About Us</Link>
            <Link to="/products" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">Products</Link>
            <Link to="/wellness-blog" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">Wellness Blog</Link>
            <Link to="/dashboard" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">Sparsh</Link>
            <Link to="/contact" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">Contact</Link>
          </div>

          {/* Search, User & CTA */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 text-gray-700 hover:text-emerald-600 cursor-pointer"
            >
              <i className="ri-search-line text-xl"></i>
            </button>
            <Link
              to={isLoggedIn ? "/profile" : "/login"}
              className="p-2 text-gray-700 hover:text-emerald-600 cursor-pointer"
            >
              <i className="ri-user-line text-xl"></i>
            </Link>
            <Button variant="primary" size="sm" onClick={handleShopNow}>Shop Now</Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 text-gray-700 hover:text-emerald-600 cursor-pointer"
            >
              <i className="ri-search-line text-xl"></i>
            </button>
            <Link
              to={isLoggedIn ? "/profile" : "/login"}
              className="p-2 text-gray-700 hover:text-emerald-600 cursor-pointer"
            >
              <i className="ri-user-line text-xl"></i>
            </Link>
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md text-gray-700 hover:text-emerald-600 cursor-pointer"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {isSearchOpen && (
          <div className="py-4 border-t border-gray-100">
            <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for Moringa products..."
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                autoFocus
              />
              <button
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-emerald-600 cursor-pointer"
              >
                <i className="ri-search-line text-xl"></i>
              </button>
            </form>

            {/* Search Results */}
            {searchQuery && (
              <div className="mt-4 max-w-2xl mx-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                {filteredProducts.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {filteredProducts.map((product) => (
                      <Link
                        key={product.id}
                        to={product.path}
                        onClick={() => {
                          setIsSearchOpen(false);
                          setSearchQuery('');
                        }}
                        className="flex items-center gap-4 p-4 hover:bg-emerald-50 cursor-pointer transition-colors"
                      >
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{product.name}</h3>
                          <p className="text-emerald-600 font-bold">₹{product.price}</p>
                        </div>
                        <i className="ri-arrow-right-line text-gray-400"></i>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <i className="ri-search-line w-12 h-12 flex items-center justify-center text-4xl text-gray-300 mx-auto mb-3"></i>
                    <p className="text-gray-500">No products found</p>
                    <p className="text-sm text-gray-400 mt-1">Try searching for "Moringa Powder" or "Tablets"</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">Home</Link>
              <Link to="/about" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">About Us</Link>
              <Link to="/products" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">Products</Link>
              <Link to="/wellness-blog" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">Wellness Blog</Link>
              <Link to="/dashboard" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">Sparsh</Link>
              <Link to="/contact" className="text-gray-700 hover:text-emerald-600 font-medium cursor-pointer">Contact</Link>
              <Button variant="primary" size="sm" className="w-fit" onClick={handleShopNow}>Shop Now</Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
